import React, { useRef, type FunctionComponent } from 'react';
import { REGEX_WHITE_SPACE } from '../../../../../constants/regular-expression';
import { SEARCH_ROUTE } from '../../../../constants/links';
import { MAX_QUERY_LENGTH } from '../../../../constants/search-bar';
import { useSearchBarReducerContext } from '../../../../contexts/search-bar/search-bar.context';
import { generateDataSelector } from '../../../../helpers/general-helper/general-helper';
import { useNavigation } from '../../../../hooks/navigation/navigation.hooks';
import { IconButton } from '../../../buttons/icon-button/icon-button.component';
import SearchIcon from '../../../svgs/untitled-ui/search.svg';
import XCloseIcon from '../../../svgs/untitled-ui/x-close.svg';

export type MobileSearchBarProps = {
	placeholder?: string;
};

/**
 * A simple mobile search bar whose main purpose is to open the typeahead search drawer.
 */
export const MobileSearchBar: FunctionComponent<MobileSearchBarProps> = ({ placeholder = 'What are you shopping for?' }) => {
	const navigate = useNavigation();
	const [state, dispatch] = useSearchBarReducerContext();
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
		event.preventDefault();
		const searchTerm = state.displayValue.replace(REGEX_WHITE_SPACE, ' ').trim();
		if (!searchTerm) {
			dispatch({ type: 'typeaheadOpened' });
			return;
		}
		dispatch({ type: 'typeaheadClosed' });
		navigate(`/search?term=${encodeURIComponent(searchTerm)}`);
	};
	const handleInputFocus = () => dispatch({ type: 'typeaheadOpened' });
	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => dispatch({ type: 'inputChanged', value: event.target.value });
	const handleClearClick = () => {
		inputRef.current?.focus(); // Focus the search input to trick iOS Safari into opening the keyboard.
		dispatch({ type: 'inputCleared' });
		dispatch({ type: 'inputBlurred' }); // Blur now so after the drawer opens, it can focus the search input inside the drawer.
	};

	return (
		<form
			role="search"
			aria-label="site wide search form"
			action={SEARCH_ROUTE}
			onSubmit={handleFormSubmit}
			className="bg-theme-white br2 flex flex-nowrap items-center flex-auto b--theme-grey-light ba">
			<input
				name="term"
				aria-label="Search"
				type="text"
				role="searchbox"
				data-automation={generateDataSelector('input', 'search')}
				value={state.displayValue}
				onFocus={handleInputFocus}
				onChange={handleInputChange}
				placeholder={placeholder}
				data-testid="SearchInput"
				autoComplete="off"
				autoCapitalize="off"
				autoCorrect="off"
				maxLength={MAX_QUERY_LENGTH}
				className="input-reset input br2 f5 lh-title bn w-100 truncate theme-grey-darker outline-0 pl3 pr0"
				ref={inputRef}
			/>
			{state.displayValue && (
				<IconButton ariaLabel="Clear search" size="ICON" buttonStyle="ICON" className="bn outline-0" onClick={handleClearClick}>
					<XCloseIcon className="db f5" />
				</IconButton>
			)}
			<div className="bl b--theme-grey-light" style={{ height: '1.5rem' }} />
			<IconButton buttonType="submit" ariaLabel="Submit search" size="ICON" buttonStyle="ICON" className="bn outline-0">
				<SearchIcon className="db f4" />
			</IconButton>
		</form>
	);
};
